import { useEffect, useRef, useState } from 'react';
import { Button } from './Button';
import { Textarea } from './Textarea';

interface ReasonDialogProps {
  title:         string;
  placeholder:   string;
  /** A reason must be typed before Confirm enables (reject, cancel); optional otherwise (approve). */
  required:      boolean;
  confirmLabel?: string;
  onConfirm:     (reason: string) => void;
  onCancel:      () => void;
}

/**
 * Ask for a reason or remark before a workflow action — approve, reject, reverse, cancel.
 *
 * Twelve document screens each carried a private copy of this dialog (ADR-004), differing only in
 * the accent colour of the Confirm button. ConfirmDialog asks yes/no; this one also takes the text
 * the action records.
 */
export const ReasonDialog = ({
  title, placeholder, required, confirmLabel = 'Confirm', onConfirm, onCancel,
}: ReasonDialogProps) => {
  const [text, setText] = useState('');
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { ref.current?.focus(); }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-[500] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onMouseDown={e => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md p-6">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-3">{title}</h3>
        <Textarea
          ref={ref}
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder={placeholder}
          rows={4}
          aria-label={title}
        />
        <div className="flex gap-3 mt-4 justify-end">
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={() => onConfirm(text)} disabled={required && !text.trim()}>{confirmLabel}</Button>
        </div>
      </div>
    </div>
  );
};
